/**
 * Profile Service
 * API client for user professional profile data management
 */
import { apiClient } from '../apiClient';

// Type Definitions

export interface Qualification {
  id?: number;
  qualification_type: string;  // nsc, higher_cert, diploma, adv_cert, adv_diploma, bachelors, btech, pgdip, honours, masters, phd, professional, international
  institution: string;
  country: 'south_africa' | 'international';
  
  // Academic details
  qualification_name?: string;
  cesm_category?: string;  // CESM code (01-20, 99)
  field_of_study?: string;
  
  // NQF details
  nqf_level?: number;
  
  // Completion status
  year_completed?: string;
  in_progress: boolean;
  expected_completion_year?: string;
  
  // International qualification fields
  international_equivalency?: string;
  saqa_evaluated?: boolean;
  saqa_reference_number?: string;
  
  // Professional certification fields
  professional_body?: string;
  membership_number?: string;
  membership_status?: 'active' | 'inactive' | 'pending';
  
  // Research component (Honours/Masters/PhD)
  thesis?: boolean;
  thesis_title?: string;
  
  // Verification
  verification_document_url?: string;
}

export interface CareerEntry {
  id?: number;
  organization: string;
  position: string;
  start_date: string;
  end_date?: string;
  organization_type?: 'education' | 'corporate' | 'government' | 'non-profit' | 'other';
  direct_reports?: number;
  extended_team_size?: number;
  budget_responsibility?: number;
  budget_currency?: string;
  key_achievements: string[];
  skills_developed: string[];
}

// Internal (Eduvos) Career History Types
export interface InternalRoleEntry {
  /** null when F&O's position isn't in the platform's positions yet. */
  position_id: number | null;
  position_code: string;
  title: string;
  department?: string;
  campus?: string;
  start_date?: string;
  end_date?: string;
  is_current: boolean;
  change_type?: 'initial' | 'promotion' | 'transfer' | 'lateral' | 'current';
}

export interface InternalCareerHistory {
  user_id: number;
  employee_id?: string;
  original_hire_date?: string;
  tenure_years?: number;
  tenure_months?: number;
  current_role?: InternalRoleEntry;
  role_history: InternalRoleEntry[];
  total_roles: number;
}

export interface ProfessionalContext {
  areas_of_expertise: string[];
  language_proficiencies: Array<{ language: string; level: string }>;
  geographic_experience: string[];
  industry_experience: string[];
  completion_status: 'incomplete' | 'partial' | 'complete';
}

export interface CompleteProfile {
  qualifications: Qualification[];
  career_history: CareerEntry[];
  professional_context: ProfessionalContext;
}

// API Service Class

class ProfileService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = '/identity/profile';
  }

  // ===== Qualifications =====

  async getQualifications(): Promise<Qualification[]> {
    try {
      const response = await apiClient.get<Qualification[]>(`${this.baseUrl}/qualifications`);
      return response.data;
    } catch (error) {
      console.error('Failed to get qualifications:', error);
      throw error;
    }
  }

  async createQualification(qualification: Omit<Qualification, 'id'>): Promise<Qualification> {
    try {
      const response = await apiClient.post<Qualification>(`${this.baseUrl}/qualifications`, qualification);
      return response.data;
    } catch (error) {
      console.error('Failed to create qualification:', error);
      throw error;
    }
  }

  async updateQualification(id: number, qualification: Omit<Qualification, 'id'>): Promise<Qualification> {
    try {
      const response = await apiClient.put<Qualification>(`${this.baseUrl}/qualifications/${id}`, qualification);
      return response.data;
    } catch (error) {
      console.error('Failed to update qualification:', error);
      throw error;
    }
  }

  async deleteQualification(id: number): Promise<void> {
    try {
      await apiClient.delete(`${this.baseUrl}/qualifications/${id}`);
    } catch (error) {
      console.error('Failed to delete qualification:', error);
      throw error;
    }
  }

  // ===== Career History =====

  async getCareerHistory(): Promise<CareerEntry[]> {
    try {
      const response = await apiClient.get<CareerEntry[]>(`${this.baseUrl}/career-history`);
      return response.data;
    } catch (error) {
      console.error('Failed to get career history:', error);
      throw error;
    }
  }

  async createCareerEntry(entry: Omit<CareerEntry, 'id'>): Promise<CareerEntry> {
    try {
      const response = await apiClient.post<CareerEntry>(`${this.baseUrl}/career-history`, entry);
      return response.data;
    } catch (error) {
      console.error('Failed to create career entry:', error);
      throw error;
    }
  }

  async updateCareerEntry(id: number, entry: Omit<CareerEntry, 'id'>): Promise<CareerEntry> {
    try {
      const response = await apiClient.put<CareerEntry>(`${this.baseUrl}/career-history/${id}`, entry);
      return response.data;
    } catch (error) {
      console.error('Failed to update career entry:', error);
      throw error;
    }
  }

  async deleteCareerEntry(id: number): Promise<void> {
    try {
      await apiClient.delete(`${this.baseUrl}/career-history/${id}`);
    } catch (error) {
      console.error('Failed to delete career entry:', error);
      throw error;
    }
  }

  // ===== Professional Context =====

  async getProfessionalContext(): Promise<ProfessionalContext | null> {
    try {
      const response = await apiClient.get<ProfessionalContext>(`${this.baseUrl}/professional-context`);
      return response.data;
    } catch (error) {
      console.error('Failed to get professional context:', error);
      throw error;
    }
  }

  async saveProfessionalContext(context: ProfessionalContext): Promise<ProfessionalContext> {
    try {
      const response = await apiClient.post<ProfessionalContext>(`${this.baseUrl}/professional-context`, context);
      return response.data;
    } catch (error) {
      console.error('Failed to save professional context:', error);
      throw error;
    }
  }

  // ===== Complete Profile =====

  async getCompleteProfile(): Promise<CompleteProfile> {
    try {
      const response = await apiClient.get<CompleteProfile>(`${this.baseUrl}/complete`);
      return response.data;
    } catch (error) {
      console.error('Failed to get complete profile:', error);
      throw error;
    }
  }

  async saveCompleteProfile(profile: CompleteProfile): Promise<CompleteProfile> {
    try {
      const response = await apiClient.post<CompleteProfile>(`${this.baseUrl}/complete`, profile);
      return response.data;
    } catch (error) {
      console.error('Failed to save complete profile:', error);
      throw error;
    }
  }

  // ===== Internal (Eduvos) Career History =====

  async getInternalCareerHistory(): Promise<InternalCareerHistory> {
    try {
      const response = await apiClient.get<InternalCareerHistory>(`${this.baseUrl}/internal-career`);
      return response.data;
    } catch (error) {
      console.error('Failed to get internal career history:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const profileService = new ProfileService();
export default profileService;
